import { airTracker } from './airTracker';
import dayjs from 'dayjs';
import { isPerformanceAPISupported, timeOrigin } from '@/constants/performance';

export type TraceEventPayload = Record<string, unknown>;

const enum DependencyState {
  Pending,
  Ready,
}

export interface PerformanceMeasureOptions {
  start?: number;
  // to allow a different event name from the measure name
  eventName?: string;
  payload?: TraceEventPayload;
  // for backward compatibility
  additionalEndName?: string;
  additionalStartName?: string;
}

class TraceManager {
  private TTVRReached = false;
  private debug = /debug=perf/.test(window.location.search);
  private TTVRDependencies = new Map([['root_component_ready', DependencyState.Pending]]);

  constructor() {
    this.mark('page_start');
  }

  public mark(event: string, payload: TraceEventPayload = {}): void {
    airTracker.info(event, {
      event: 'TRACE_EVENT',
      timeElapsed: this.now(),
      ...this.getTimeStampPayload(),
      ...payload,
    });
    if (isPerformanceAPISupported) {
      performance.mark(event);
    }
    this.markTTVRDependency(event);
  }

  public measure(
    name: string,
    { start, eventName = name, additionalStartName, additionalEndName, payload }: PerformanceMeasureOptions = {
      start: 0,
    }
  ) {
    if (!isPerformanceAPISupported) return;

    const measure = performance.measure(name, { start });
    const { startTime, duration } = measure;
    const endTime = startTime + duration;
    airTracker.info(eventName, {
      event: 'TRACE_EVENT',
      duration,
      startTime,
      endTime,
      ...this.getTimeStampPayload(),
      ...(additionalStartName ? { [additionalStartName]: startTime } : {}),
      ...(additionalEndName ? { [additionalEndName]: endTime } : {}),
      ...payload,
    });
    this.markTTVRDependency(name);
  }

  private markTTVRDependency(name: string) {
    if (this.TTVRDependencies.get(name) !== DependencyState.Pending) return;
    this.TTVRDependencies.set(name, DependencyState.Ready);

    if (this.TTVRReached) return;
    this.TTVRReached = [...this.TTVRDependencies].every(([_, reached]) => reached);

    this.debug && console.log({ TTVRReached: this.TTVRReached, deps: this.TTVRDependencies });
    if (this.TTVRReached) this.measure('TTVR');
  }

  public addTTVRDependency(marker: string) {
    if (!this.TTVRDependencies.has(marker)) this.TTVRDependencies.set(marker, DependencyState.Pending);
  }

  private getTimeStampPayload() {
    const timestamp = Date.now();
    return {
      timestamp,
      timestampFormatted: dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss.SSS'),
    };
  }

  private now(): number {
    if (isPerformanceAPISupported) {
      return performance.now();
    }
    return Date.now() - timeOrigin;
  }
}

export const trace = new TraceManager();
